import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Layout, { PageHeader } from "@finance/components/Layout";
import { Button } from "@finance/components/ui/button";
import { api, errMsg, fmtNum } from "@finance/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@finance/components/ui/dialog";
import { UploadSimple, FileArrowDown, Check, X, Package, ShoppingCart, Truck, Eye, ArrowSquareOut } from "@phosphor-icons/react";

const TABS = [
  { k: "po", label: "Sales Order PO Requests", icon: ShoppingCart },
  { k: "stock", label: "Stock Order PO Requests", icon: Package },
  { k: "payments", label: "Logistics Payment Requests", icon: Truck },
];
const PO_STATUS = {
  requested: ["Awaiting Review", "bg-amber-50 text-amber-700 border-amber-200"],
  approved: ["Approved · Upload PO", "bg-blue-50 text-blue-700 border-blue-200"],
  rejected: ["Rejected", "bg-red-50 text-red-700 border-red-200"],
  issued: ["PO Issued", "bg-emerald-50 text-emerald-700 border-emerald-200"],
};
const PAY_STATUS = {
  pending: ["Awaiting Review", "bg-amber-50 text-amber-700 border-amber-200"],
  approved: ["Approved · Upload POP", "bg-blue-50 text-blue-700 border-blue-200"],
  rejected: ["Rejected", "bg-red-50 text-red-700 border-red-200"],
  paid: ["Paid", "bg-emerald-50 text-emerald-700 border-emerald-200"],
};
const ATT = [["client_po", "Client PO"], ["supplier_quote", "Final Supplier Quote"], ["client_quote", "Our Client Quote"], ["costing", "Costing Snapshot"]];
const toB64 = (f) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });
const qty = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

async function fetchBlob(path) {
  const res = await api.get(path, { responseType: "blob" });
  const type = res.headers["content-type"] || "application/octet-stream";
  return { url: window.URL.createObjectURL(new Blob([res.data], { type })), type };
}

function DocViewer({ docs, start, title, onClose }) {
  const [idx, setIdx] = useState(start);
  const [file, setFile] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { setIdx(start); }, [start]);
  useEffect(() => {
    if (idx === null || !docs[idx]) return undefined;
    let url = "";
    setFile(null); setErr("");
    fetchBlob(docs[idx].path).then((f) => { url = f.url; setFile(f); }).catch((e) => setErr(errMsg(e)));
    return () => { if (url) window.URL.revokeObjectURL(url); };
  }, [idx, docs]);
  const canInline = file && (/pdf|image\//i.test(file.type));
  return (
    <Dialog open={idx !== null} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-6xl" data-testid="ops-doc-viewer">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="grid gap-3 md:grid-cols-[220px_1fr]">
          <div className="flex flex-row gap-1.5 overflow-x-auto md:flex-col">
            {docs.map((d, i) => (
              <button key={d.path} onClick={() => setIdx(i)} className={`shrink-0 rounded-md border px-3 py-2 text-left text-xs transition-colors ${i === idx ? "border-[#1B4383] bg-[#EEF3FA] text-[#1B4383]" : "bg-white hover:bg-slate-50"}`} data-testid={`ops-doc-tab-${i}`}>
                <div className="font-semibold">{d.label}</div><div className="truncate text-[10px] text-muted-foreground">{d.filename}</div>
              </button>
            ))}
          </div>
          <div className="flex h-[70vh] items-center justify-center overflow-hidden rounded-md border bg-slate-50" data-testid="ops-doc-preview">
            {err ? <p className="text-sm text-red-600">{err}</p> : !file ? <p className="text-sm text-muted-foreground">Loading…</p> : canInline ? (
              /image\//i.test(file.type) ? <img src={file.url} alt={docs[idx]?.label} className="max-h-full max-w-full object-contain" /> : <iframe title="document" src={file.url} className="h-full w-full" />
            ) : (
              <div className="text-center text-sm text-muted-foreground">
                <p className="mb-3">This file type can't be previewed here.</p>
                <Button size="sm" onClick={() => window.open(file.url, "_blank")} className="gap-1.5" data-testid="ops-doc-open"><ArrowSquareOut size={14} />Open / Download</Button>
              </div>
            )}
          </div>
        </div>
        {file && canInline && <div className="flex justify-end"><Button size="sm" variant="outline" onClick={() => window.open(file.url, "_blank")} className="gap-1.5" data-testid="ops-doc-newtab"><ArrowSquareOut size={14} />Open In New Tab</Button></div>}
      </DialogContent>
    </Dialog>
  );
}

function DocsRow({ docs, title, empty }) {
  const [open, setOpen] = useState(null);
  if (!docs.length) return <div className="mt-3 text-xs text-muted-foreground">{empty}</div>;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <Button size="sm" onClick={() => setOpen(0)} className="h-8 gap-1.5 bg-slate-900 hover:bg-slate-800" data-testid="ops-review-docs"><Eye size={14} />Review Documents ({docs.length})</Button>
      {docs.map((d, i) => <FileBtn key={d.path} label={d.label} onClick={() => setOpen(i)} testid={d.testid} />)}
      <DocViewer docs={docs} start={open} title={title} onClose={() => setOpen(null)} />
    </div>
  );
}

const Pill = ({ map, status, testid }) => {
  const [label, cls] = map[status] || Object.values(map)[0];
  return <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${cls}`} data-testid={testid}>{label}</span>;
};

const FileBtn = ({ label, onClick, testid }) => (
  <Button size="sm" variant="outline" onClick={onClick} className="h-8 gap-1.5" data-testid={testid}><FileArrowDown size={14} />{label}</Button>
);

function UploadBtn({ label, onFile, busy, testid }) {
  return (
    <label className={`inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md bg-[#1B4383] px-3 text-sm font-medium text-white hover:bg-[#122E5C] ${busy ? "pointer-events-none opacity-60" : ""}`} data-testid={testid}>
      <UploadSimple size={14} />{label}
      <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.xls,.xlsx,.doc,.docx" onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onFile({ file: await toB64(f), filename: f.name, content_type: f.type || "application/pdf" }); }} />
    </label>
  );
}

function Decide({ status, pendingKey, approvedKey, onApprove, onReject, onUpload, uploadLabel, busy, id }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {status === pendingKey && <Button size="sm" onClick={onApprove} disabled={busy} className="h-8 gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid={`ops-approve-${id}`}><Check size={14} />Approve</Button>}
      {[pendingKey, approvedKey].includes(status) && <Button size="sm" variant="outline" onClick={onReject} disabled={busy} className="h-8 gap-1.5 border-red-200 text-red-600 hover:bg-red-50" data-testid={`ops-reject-${id}`}><X size={14} />Reject</Button>}
      {[approvedKey, "issued", "paid"].includes(status) && <UploadBtn label={["issued", "paid"].includes(status) ? `Replace ${uploadLabel}` : `Upload ${uploadLabel}`} onFile={onUpload} busy={busy} testid={`ops-upload-${id}`} />}
    </div>
  );
}

function PoCard({ c, stock, onChanged }) {
  const [busy, setBusy] = useState(false);
  const req = c.request || {};
  const act = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); onChanged(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const reject = () => { const reason = window.prompt("Reason for rejecting (shown to Operations):"); if (reason !== null) act(() => api.post(`/ops-requests/po/${c.id}/reject`, { reason }), "Request rejected"); };
  const docs = [
    ...ATT.filter(([k]) => c.attachments?.[k]).map(([k, lbl]) => ({ label: lbl, filename: c.attachments[k].filename, path: `/ops-requests/po/${c.id}/file/${k}`, testid: `ops-po-file-${k}-${c.id}` })),
    ...(c.po_file ? [{ label: "Issued PO", filename: c.po_file.filename, path: `/ops-requests/po/${c.id}/file/po`, testid: `ops-po-file-po-${c.id}` }] : []),
  ];
  return (
    <div className="rounded-lg border bg-white p-4" data-testid={`ops-po-${c.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-slate-900">{c.supplier_name || "Supplier"}</span>
            <Pill map={PO_STATUS} status={req.status} testid={`ops-po-status-${c.id}`} />
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {stock ? "Stock order" : <>Sales Order <b>{c.order_no}</b>{c.client_name ? ` · ${c.client_name}` : ""}{c.rfq_code ? ` · ${c.rfq_code}` : ""}</>}
            {" · "}Requested by {req.requested_by || "Operations"}{req.requested_at ? ` on ${new Date(req.requested_at).toLocaleDateString()}` : ""}
          </div>
          {req.note && <div className="mt-1 text-xs italic text-muted-foreground">“{req.note}”</div>}
          {req.status === "rejected" && req.reject_reason && <div className="mt-1 text-xs text-red-600">Rejected: {req.reject_reason}</div>}
        </div>
        <Decide status={req.status} pendingKey="requested" approvedKey="approved" busy={busy} id={c.id} uploadLabel="Issued PO"
          onApprove={() => act(() => api.post(`/ops-requests/po/${c.id}/approve`), "Approved — now upload the issued PO")}
          onReject={reject}
          onUpload={(f) => act(() => api.post(`/ops-requests/po/${c.id}/issue`, f), "Issued PO sent to Operations")} />
      </div>
      <DocsRow docs={docs} title={`${c.supplier_name || "Supplier"} — Documents`} empty="No supporting documents attached." />
      {stock && c.lines?.length > 0 && (
        <table className="mt-3 w-full text-xs">
          <thead><tr className="text-left text-muted-foreground"><th className="py-1 font-medium">SKU</th><th className="py-1 font-medium">Description</th><th className="py-1 text-right font-medium">Qty</th><th className="py-1 text-right font-medium">Unit Price ({c.currency})</th></tr></thead>
          <tbody>{c.lines.map((l) => <tr key={l.product_id} className="border-t"><td className="py-1 font-mono">{l.sku}</td><td className="py-1">{l.description}</td><td className="py-1 text-right">{qty(l.quantity)} {l.uom}</td><td className="py-1 text-right">{qty(l.unit_price)}</td></tr>)}</tbody>
        </table>
      )}
    </div>
  );
}

function PayCard({ r, onChanged }) {
  const [busy, setBusy] = useState(false);
  const act = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); onChanged(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const reject = () => { const reason = window.prompt("Reason for rejecting (shown to Logistics):"); if (reason !== null) act(() => api.post(`/ops-requests/payments/${r.id}/reject`, { reason }), "Request rejected"); };
  return (
    <div className="rounded-lg border bg-white p-4" data-testid={`ops-pay-${r.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold capitalize text-slate-900">{r.category} · {r.currency} {fmtNum(r.amount)}</span>
            <Pill map={PAY_STATUS} status={r.status} testid={`ops-pay-status-${r.id}`} />
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">Shipment <b>{r.shipment_no}</b>{r.client_name ? ` · ${r.client_name}` : ""}{r.payee ? ` · Pay ${r.payee}` : ""} · Requested by {r.created_by_name || "Logistics"}{r.created_at ? ` on ${new Date(r.created_at).toLocaleDateString()}` : ""}</div>
          {r.note && <div className="mt-1 text-xs italic text-muted-foreground">“{r.note}”</div>}
          {r.status === "rejected" && r.reject_reason && <div className="mt-1 text-xs text-red-600">Rejected: {r.reject_reason}</div>}
        </div>
        <Decide status={r.status} pendingKey="pending" approvedKey="approved" busy={busy} id={r.id} uploadLabel="Proof Of Payment"
          onApprove={() => act(() => api.post(`/ops-requests/payments/${r.id}/approve`), "Approved — upload the proof of payment once paid")}
          onReject={reject}
          onUpload={(f) => act(() => api.post(`/ops-requests/payments/${r.id}/pop`, f), "Marked paid")} />
      </div>
      <DocsRow docs={[
        ...(r.has_support ? [{ label: "Supporting Document", filename: r.support_filename, path: `/ops-requests/payments/${r.id}/file/support`, testid: `ops-pay-support-${r.id}` }] : []),
        ...(r.has_pop ? [{ label: "Proof Of Payment", filename: r.pop_filename, path: `/ops-requests/payments/${r.id}/file/pop`, testid: `ops-pay-pop-${r.id}` }] : []),
      ]} title={`Shipment ${r.shipment_no} — ${r.category} payment`} empty="No supporting document attached." />
    </div>
  );
}

const OPEN = { po: ["requested", "approved"], stock: ["requested", "approved"], payments: ["pending", "approved"] };

export default function OpsRequests() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.k === params.get("tab")) ? params.get("tab") : "po";
  const [data, setData] = useState({ po: null, stock: null, payments: null });
  const [showAll, setShowAll] = useState(false);
  const load = useCallback(() => {
    window.dispatchEvent(new Event("ops-requests-changed"));
    Promise.all([api.get("/ops-requests/po?kind=sales"), api.get("/ops-requests/po?kind=stock"), api.get("/ops-requests/payments")])
      .then(([a, b, c]) => setData({ po: a.data, stock: b.data, payments: c.data })).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  const statusOf = (x) => (tab === "payments" ? x.status : x.request?.status);
  const rows = data[tab];
  const shown = (rows || []).filter((x) => showAll || OPEN[tab].includes(statusOf(x)));
  const openCount = (k) => (data[k] || []).filter((x) => OPEN[k].includes(k === "payments" ? x.status : x.request?.status)).length;
  return (
    <Layout>
      <PageHeader title="Operations Hub Requests" subtitle="Review what Operations sends over — view the documents, approve or reject, then upload the issued PO or proof of payment. Nothing here posts to your books." />
      <div className="p-4 sm:p-8">
        <div className="mb-4 flex flex-wrap gap-2" data-testid="ops-tabs">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setParams({ tab: t.k })} className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${tab === t.k ? "border-[#1B4383] bg-[#1B4383] text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`} data-testid={`ops-tab-${t.k}`}>
              <t.icon size={16} />{t.label}
              {openCount(t.k) > 0 && <span className={`rounded-full px-1.5 text-[11px] font-bold ${tab === t.k ? "bg-white text-[#1B4383]" : "bg-amber-100 text-amber-700"}`}>{openCount(t.k)}</span>}
            </button>
          ))}
        </div>
        <label className="mb-3 flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} data-testid="ops-show-all" />Show completed &amp; rejected too</label>
        {rows === null ? <p className="text-sm text-muted-foreground">Loading…</p> : !shown.length ? (
          <div className="rounded-lg border border-dashed bg-white p-10 text-center text-sm text-muted-foreground" data-testid="ops-empty">Nothing waiting here.</div>
        ) : (
          <div className="space-y-3">
            {shown.map((x) => (tab === "payments" ? <PayCard key={x.id} r={x} onChanged={load} /> : <PoCard key={x.id} c={x} stock={tab === "stock"} onChanged={load} />))}
          </div>
        )}
      </div>
    </Layout>
  );
}
